<template>
  <video
    ref="video"
    :width="drop.preview_image.width"
    :height="drop.preview_image.height"
    :alt="drop.alt"
    :muted="muted ?? true"
    :controls="showControls"
    autoplay
    loop
    playsinline
    preload="metadata"
    class="vue-video"
  >
    <template v-if="sourceReady">
      <source
        v-for="(source, index) in drop.sources"
        :key="index"
        :src="source.url"
        :type="source.mime_type"
        :width="source.width"
        :height="source.height"
      >
    </template>
  </video>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

import type { VideoDrop } from '@/types/shopify/video'

export interface Props {
  id: string
  button: string
  pauseButton?: string
  desktop: VideoDrop
  mobile?: VideoDrop
  showControls?: boolean
  muted?: boolean
  abActive?: boolean
}
const mq = window.matchMedia('(max-width: 767px)')
const props = defineProps<Props>()
const video = ref()
const drop = ref<VideoDrop>(mq.matches && props.mobile ? props.mobile : props.desktop)
const sourceReady = ref(!props.abActive)
const playing = ref(true)

onMounted(() => {
  const el = video.value
  if (!el || props.showControls) {
    return
  }
  const parent = el.parentNode
  // remove poster placeholders
  // parent.querySelectorAll('.video-poster').forEach(i => parent.removeChild(i))

  // add play button
  const btn = document.createElement('button')
  btn.setAttribute('aria-label', 'play video')
  parent.appendChild(btn)
  el.addEventListener('suspend', () => btn.style.opacity = '1')
  if (!props.pauseButton) {
    return
  }
  el.addEventListener('pause', () => bindPause(btn))
  el.addEventListener('play', () => bindPlay(btn))
  btn.addEventListener('click', () => bindClick(el))
})

const bindClick = (el: HTMLVideoElement) => playing.value ? el.pause() : el.play()

const bindPlay = (btn: HTMLButtonElement) => {
  playing.value = true
  btn.setAttribute('aria-label', 'pause video')
  btn.style.backgroundImage = `url("${props.pauseButton}")`
  btn.classList.add('vue-video-pause-button')
  btn.classList.remove('vue-video-play-button')
}

const bindPause = (btn: HTMLButtonElement) => {
  playing.value = false
  btn.setAttribute('aria-label', 'play video')
  btn.style.backgroundImage = `url("${props.button}")`
  btn.classList.add('vue-video-play-button')
  btn.classList.remove('vue-video-pause-button')
}

let autoplayTimeout: ReturnType<typeof setTimeout>

if (props.abActive) {
  document.addEventListener(`ab:video-swap:${props.id}`, (e: Event) => {
    const detail = JSON.parse((<CustomEvent>e).detail)
    const { value } = video
    if (value && typeof detail === 'object' && 'desktop' in detail && 'mobile' in detail) {
      clearTimeout(autoplayTimeout)
      value.pause()
      drop.value = (mq.matches ? detail.mobile : detail.desktop) as VideoDrop
      sourceReady.value = true
      value.load()
      value.play()
    }
  })
}

document.addEventListener('DOMContentLoaded', () => {
  // wait 50ms to allow for AB tests to change video source
  autoplayTimeout = setTimeout(() => {
    sourceReady.value = true
  }, 50)
})

// Use this in AB test to trigger video swap
// document.addEventListener('DOMContentLoaded', () => {
//   setTimeout(() => {
//     var event = new CustomEvent('ab:video-swap', { detail: 'seanlew' })
//     document.dispatchEvent(event)
//   }, 10)
// })
</script>
